import { useMemo } from 'react';

import {
  type Capability,
  type Entitlements,
  type PlanKey,
  entitlementsForPlan,
  resolvePlanKey,
} from '@chartai/utils';

import { useStripePlans } from './usePlans';
import { useSubscriptionStatus } from './useSubscription';
import { useUserDocument } from './useUser';

interface EntitlementsResult {
  planKey: PlanKey;
  entitlements: Entitlements;
  can: (capability: Capability) => boolean;
  isLoading: boolean;
}

/**
 * CAB-602 — what the signed-in user's plan unlocks.
 *
 * Resolves the plan from `subscription.priceId` against the Firestore plans
 * array (the same source the pricing and billing pages use), then reads the
 * entitlements table.
 *
 * Fails closed in every uncertain case — loading, signed out, inactive
 * subscription, unknown price id — so a capability is never granted by
 * accident. Callers that need to distinguish "denied" from "not known yet"
 * should check `isLoading` before rendering an upgrade prompt, otherwise the
 * prompt flashes for paying users on first paint.
 */
export function useEntitlements(uid: string): EntitlementsResult {
  const { data: userDoc, isLoading: userLoading } = useUserDocument(uid || null);
  const { isActive, isLoading: subLoading } = useSubscriptionStatus(uid);
  const { plans, isLoading: plansLoading } = useStripePlans();

  const isLoading = userLoading || subLoading || plansLoading;

  const planKey = useMemo(() => {
    const sub =
      typeof userDoc?.['subscription'] === 'object' && userDoc['subscription'] !== null
        ? (userDoc['subscription'] as Record<string, unknown>)
        : null;
    const priceId = typeof sub?.['priceId'] === 'string' ? sub['priceId'] : null;
    return resolvePlanKey(plans, priceId, isActive);
  }, [userDoc, plans, isActive]);

  const entitlements = useMemo(() => entitlementsForPlan(planKey), [planKey]);

  return useMemo(
    () => ({
      planKey,
      entitlements,
      can: (capability: Capability) => entitlements[capability],
      isLoading,
    }),
    [planKey, entitlements, isLoading],
  );
}
